import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';

export interface UsageResponse {
  count: number;
  total_results: number;
  result: UsageDetail[];
}

export interface UsageDetail {
  id: number;
  period_start: string | null;
  period_end: string | null;
  stt_units_used: number;
  alignment_units_used: number;
  translation_characters_used: number;
  billed: boolean;
  cost_billed: string;
  billable_cost: string;
  custom_models_trained: number;
  custom_models_hosted: number;
}

export const useGetUsage = (subscriptionId?: string) => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;
  const sort = (searchParams.sort as string | undefined) || undefined;
  const direction =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;
  const q = (searchParams.q as string | undefined) || undefined;

  const fetchUsage = async () => {
    if (!subscriptionId) throw new Error('subscriptionId is required');

    const { usageById } = GET_URL_NEW(); // <- MUST be the version that includes usageById
    const url = usageById(subscriptionId, {
      offset,
      limit,
      q,
      sort,
      direction
    });

    const { data } = await Axios.get<UsageResponse>(url);
    return data;
  };

  return useQuery<UsageResponse, AxiosError<BaseError>>(
    ['usage', subscriptionId, offset, limit, q, sort, direction],
    fetchUsage,
    {
      enabled: !!subscriptionId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      onError: error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-has-occurred');
        toast.error(String(msg));
      }
    }
  );
};
